import React, { useState, useRef } from "react";
import { getToken } from "_services/user-service";
import axios from "axios";
import { jsPDF } from "jspdf";
import html2canvas from "html2canvas";

// Bootstrap
import { Row, Col, Form, Button, Spinner, Alert, Badge } from "react-bootstrap";

// Tremor
import {
    Title,
    Text,
    Metric,
    Flex,
    Card,
    BadgeDelta,
} from "@tremor/react";

// Styles
import { Colors } from "_styles";

// Icons
import { FiDownload, FiAward, FiTrendingUp, FiUsers, FiStar, FiAlertCircle } from "react-icons/fi";

const AVAILABLE_AIRPORTS = [
    "BFS", "DUB", "ORK", "SNN", "GLA", "EDI", "BRS", "JER", "PUJ"
];

const PerformanceReport = () => {
    const reportRef = useRef(null);

    // Filter states
    const [year, setYear] = useState(new Date().getFullYear());
    const [startDate, setStartDate] = useState("");
    const [endDate, setEndDate] = useState("");
    const [airportOptions, setAirportOptions] = useState(AVAILABLE_AIRPORTS);
    const [selectedAirports, setSelectedAirports] = useState([]);
    const [newAirportCode, setNewAirportCode] = useState("");
    const [airportCodeError, setAirportCodeError] = useState("");
    const [filterMode, setFilterMode] = useState("year"); // "year" or "dateRange"
    const [includeReviews, setIncludeReviews] = useState(false); // pull individual reviews for a single airport

    // Data states
    const [reportData, setReportData] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [downloadingPdf, setDownloadingPdf] = useState(false);

    // Generate year options (2020 to current year + 1)
    const yearOptions = [];
    const currentYear = new Date().getFullYear();
    for (let y = 2020; y <= currentYear + 1; y++) {
        yearOptions.push(y);
    }

    // Fetch report data
    const fetchReport = async () => {
        // Individual reviews pull full booking docs, so the server requires exactly one airport.
        if (includeReviews && selectedAirports.length !== 1) {
            setError("Select exactly one airport to include individual reviews.");
            return;
        }

        setLoading(true);
        setError(null);

        try {
            const token = await getToken();

            // Build query params
            const params = new URLSearchParams();

            if (filterMode === "year") {
                params.append("year", year);
            } else {
                if (startDate) params.append("startDate", startDate);
                if (endDate) params.append("endDate", endDate);
            }

            if (selectedAirports.length > 0) {
                params.append("airports", selectedAirports.join(","));
            }

            if (includeReviews) {
                params.append("includeReviews", "true");
            }

            const url = `/api/v1/review/reviews/yearlyAirportRankings?${params.toString()}`;

            const response = await axios.get(url, {
                headers: { "Firebase-UID": token },
            });

            if (response.data.success) {
                setReportData(response.data);
            } else {
                setError("Failed to fetch report data");
            }
        } catch (err) {
            setError(err.response?.data?.message || err.message || "Failed to fetch report");
        } finally {
            setLoading(false);
        }
    };

    // Handle airport selection. With includeReviews on, only one airport may be selected.
    const toggleAirport = (airport) => {
        setSelectedAirports(prev => {
            if (includeReviews) {
                return prev.includes(airport) ? [] : [airport];
            }
            return prev.includes(airport)
                ? prev.filter(a => a !== airport)
                : [...prev, airport];
        });
    };

    // Toggle the "include individual reviews" switch. Turning it on forces a single
    // airport selection, so trim any extra selections down to the first.
    const toggleIncludeReviews = () => {
        setIncludeReviews(prev => {
            const next = !prev;
            if (next && selectedAirports.length > 1) {
                setSelectedAirports([selectedAirports[0]]);
            }
            return next;
        });
    };

    const handleAirportCodeChange = (event) => {
        const value = event.target.value.toUpperCase().replace(/[^A-Z]/g, "").slice(0, 3);
        setNewAirportCode(value);
        setAirportCodeError("");
    };

    const addAirportCode = () => {
        const airportCode = newAirportCode.trim();

        if (airportCode.length !== 3) {
            setAirportCodeError("Enter a 3-letter IATA code.");
            return;
        }

        if (!airportOptions.includes(airportCode)) {
            setAirportOptions(prev => [...prev, airportCode].sort());
        }

        setSelectedAirports(prev => {
            if (includeReviews) return [airportCode];
            return prev.includes(airportCode) ? prev : [...prev, airportCode];
        });
        setNewAirportCode("");
        setAirportCodeError("");
    };

    const handleAirportCodeKeyDown = (event) => {
        if (event.key === "Enter") {
            event.preventDefault();
            addAirportCode();
        }
    };

    // Download as PDF
    const handleDownloadPdf = async () => {
        if (!reportRef.current || !reportData) return;

        setDownloadingPdf(true);

        try {
            const element = reportRef.current;
            const canvas = await html2canvas(element, {
                scale: 1.5, // Reduced from 2 for smaller file size
                useCORS: true,
                logging: false,
                backgroundColor: "#ffffff",
            });

            // Use JPEG with compression instead of PNG
            const imgData = canvas.toDataURL("image/jpeg", 0.7); // 0.7 quality = good balance

            const pdf = new jsPDF({
                orientation: "portrait",
                unit: "mm",
                format: "a4",
                compress: true, // Enable PDF compression
            });

            const pageWidth = pdf.internal.pageSize.getWidth();
            const pageHeight = pdf.internal.pageSize.getHeight();
            const imgWidth = pageWidth - 20;
            const imgHeight = (canvas.height * imgWidth) / canvas.width;

            let heightLeft = imgHeight;
            let position = 10;

            pdf.addImage(imgData, "JPEG", 10, position, imgWidth, imgHeight, undefined, "FAST");
            heightLeft -= pageHeight - 20;

            while (heightLeft > 0) {
                position = heightLeft - imgHeight + 10;
                pdf.addPage();
                pdf.addImage(imgData, "JPEG", 10, position, imgWidth, imgHeight, undefined, "FAST");
                heightLeft -= pageHeight - 20;
            }

            const fileName = `performance-report-${reportData.year || "custom"}-${new Date().toISOString().split("T")[0]}.pdf`;
            pdf.save(fileName);
        } catch (err) {
            console.error("PDF generation error:", err);
            alert("Failed to generate PDF. Please try again.");
        } finally {
            setDownloadingPdf(false);
        }
    };

    // Get rank badge color
    const getRankColor = (rank) => {
        if (rank === 1) return "#FFD700"; // Gold
        if (rank === 2) return "#C0C0C0"; // Silver
        if (rank === 3) return "#CD7F32"; // Bronze
        return Colors.GRAY_MEDIUM;
    };

    // Render a rating as filled blue stars (1–5).
    const renderStars = (rating) => {
        const n = Math.round(Number(rating) || 0);
        if (!n) return <span style={{ color: Colors.GRAY_MEDIUM }}>—</span>;
        return (
            <span style={{ color: Colors.PRM_LIGHT_BLUE, fontSize: 16, letterSpacing: 2 }}>
                {"★".repeat(n)}
            </span>
        );
    };

    const formatReviewDate = (value) => {
        if (!value) return "—";
        const d = new Date(value);
        return isNaN(d.getTime()) ? value : d.toLocaleDateString();
    };

    const renderCategoryLeader = (title, leader, displayKey) => (
        <Card>
            <Title>{title}</Title>
            {leader ? (
                <Flex className="mt-2" alignItems="center">
                    <div>
                        <Metric>{leader.airport}</Metric>
                        <Text>Score: {leader[displayKey]}</Text>
                    </div>
                    <BadgeDelta deltaType="increase" size="lg">
                        #{leader.rank}
                    </BadgeDelta>
                </Flex>
            ) : (
                <div style={styles.emptyMetric}>
                    <Text>No ranking available for the selected period</Text>
                </div>
            )}
        </Card>
    );

    return (
        <div style={{ padding: "20px 0" }}>
            {/* Filters Section */}
            <Card className="mb-4">
                <Title>Performance Report Filters</Title>
                <Text className="mb-4">Generate airport performance rankings based on review data</Text>

                <Row className="align-items-end">
                    {/* Filter Mode Toggle */}
                    <Col md={2}>
                        <Form.Group>
                            <Form.Label style={styles.label}>Filter By</Form.Label>
                            <Form.Select
                                value={filterMode}
                                onChange={(e) => setFilterMode(e.target.value)}
                                style={styles.input}
                            >
                                <option value="year">Year</option>
                                <option value="dateRange">Date Range</option>
                            </Form.Select>
                        </Form.Group>
                    </Col>

                    {/* Year or Date Range */}
                    {filterMode === "year" ? (
                        <Col md={2}>
                            <Form.Group>
                                <Form.Label style={styles.label}>Year</Form.Label>
                                <Form.Select
                                    value={year}
                                    onChange={(e) => setYear(parseInt(e.target.value))}
                                    style={styles.input}
                                >
                                    {yearOptions.map(y => (
                                        <option key={y} value={y}>{y}</option>
                                    ))}
                                </Form.Select>
                            </Form.Group>
                        </Col>
                    ) : (
                        <>
                            <Col md={2}>
                                <Form.Group>
                                    <Form.Label style={styles.label}>Start Date</Form.Label>
                                    <Form.Control
                                        type="date"
                                        value={startDate}
                                        onChange={(e) => setStartDate(e.target.value)}
                                        style={styles.input}
                                    />
                                </Form.Group>
                            </Col>
                            <Col md={2}>
                                <Form.Group>
                                    <Form.Label style={styles.label}>End Date</Form.Label>
                                    <Form.Control
                                        type="date"
                                        value={endDate}
                                        onChange={(e) => setEndDate(e.target.value)}
                                        style={styles.input}
                                    />
                                </Form.Group>
                            </Col>
                        </>
                    )}

                    {/* Generate Button */}
                    <Col md={2}>
                        <Button
                            onClick={fetchReport}
                            disabled={loading}
                            style={styles.generateBtn}
                        >
                            {loading ? (
                                <><Spinner size="sm" className="me-2" /> Loading...</>
                            ) : (
                                "Generate Report"
                            )}
                        </Button>
                    </Col>
                </Row>

                {/* Airport Selection */}
                <div className="mt-4">
                    <Form.Label style={styles.label}>Filter by Airports (Optional)</Form.Label>
                    <div style={styles.addAirportRow}>
                        <Form.Control
                            type="text"
                            value={newAirportCode}
                            onChange={handleAirportCodeChange}
                            onKeyDown={handleAirportCodeKeyDown}
                            placeholder="IATA"
                            maxLength={3}
                            isInvalid={!!airportCodeError}
                            style={{ ...styles.input, ...styles.airportCodeInput }}
                        />
                        <Button
                            variant="outline-primary"
                            size="sm"
                            onClick={addAirportCode}
                            disabled={newAirportCode.length !== 3}
                            style={styles.addAirportBtn}
                        >
                            Add Airport
                        </Button>
                        {airportCodeError && (
                            <Text style={styles.airportCodeError}>{airportCodeError}</Text>
                        )}
                    </div>
                    <div style={styles.airportGrid}>
                        {airportOptions.map(airport => (
                            <Badge
                                key={airport}
                                bg={selectedAirports.includes(airport) ? "primary" : "light"}
                                text={selectedAirports.includes(airport) ? "white" : "dark"}
                                style={{
                                    ...styles.airportBadge,
                                    cursor: "pointer",
                                    border: selectedAirports.includes(airport)
                                        ? "none"
                                        : `1px solid ${Colors.GRAY_MEDIUM}`,
                                }}
                                onClick={() => toggleAirport(airport)}
                            >
                                {airport}
                            </Badge>
                        ))}
                        {selectedAirports.length > 0 && (
                            <Button
                                variant="link"
                                size="sm"
                                onClick={() => setSelectedAirports([])}
                                style={{ fontSize: 12 }}
                            >
                                Clear All
                            </Button>
                        )}
                    </div>

                    {/* Include individual reviews toggle (single airport only) */}
                    <div className="mt-3">
                        <Form.Check
                            type="switch"
                            id="include-reviews-switch"
                            label="Include individual reviews (single airport only)"
                            checked={includeReviews}
                            onChange={toggleIncludeReviews}
                        />
                        {includeReviews && (
                            <Text style={{ fontSize: 12, color: Colors.GRAY_DARK, marginTop: 4 }}>
                                Select exactly one airport — the report will list every review for it.
                            </Text>
                        )}
                    </div>
                </div>
            </Card>

            {/* Error Display */}
            {error && (
                <Alert variant="danger" className="mb-4">
                    <FiAlertCircle className="me-2" />
                    {error}
                </Alert>
            )}

            {/* Report Content */}
            {reportData && (
                <>
                    {/* Download Button */}
                    <div className="d-flex justify-content-end mb-3">
                        <Button
                            variant="outline-primary"
                            onClick={handleDownloadPdf}
                            disabled={downloadingPdf}
                            style={styles.downloadBtn}
                        >
                            {downloadingPdf ? (
                                <><Spinner size="sm" className="me-2" /> Generating PDF...</>
                            ) : (
                                <><FiDownload className="me-2" /> Download PDF</>
                            )}
                        </Button>
                    </div>

                    {/* Report Content Ref for PDF */}
                    <div ref={reportRef} style={{ backgroundColor: "#fff", padding: "20px" }}>
                        {/* Report Header */}
                        <div style={styles.reportHeader}>
                            <Title style={{ color: "#fff", marginBottom: 4 }}>
                                {reportData.reportTitle}
                            </Title>
                            <Text style={{ color: "rgba(255,255,255,0.8)" }}>
                                {reportData.dateRange.start} to {reportData.dateRange.end}
                            </Text>
                        </div>

                        {/* Summary Cards */}
                        <Row className="mt-4 g-3">
                            <Col xs={12} sm={6} md={3}>
                                <Card decoration="top" decorationColor="blue">
                                    <Flex alignItems="start">
                                        <div>
                                            <Text>Total Airports</Text>
                                            <Metric>{reportData.summary.totalAirports}</Metric>
                                        </div>
                                        <FiUsers size={24} color={Colors.PRM_LIGHT_BLUE} />
                                    </Flex>
                                </Card>
                            </Col>

                            <Col xs={12} sm={6} md={3}>
                                <Card decoration="top" decorationColor="emerald">
                                    <Flex alignItems="start">
                                        <div>
                                            <Text>Total Reviews</Text>
                                            <Metric>{reportData.summary.totalReviews}</Metric>
                                        </div>
                                        <FiStar size={24} color={Colors.DARK_GREEN} />
                                    </Flex>
                                </Card>
                            </Col>

                            <Col xs={12} sm={6} md={3}>
                                <Card decoration="top" decorationColor="amber">
                                    <Flex alignItems="start">
                                        <div>
                                            <Text>Avg Quality Score</Text>
                                            <Metric>{reportData.summary.averages.qualityDisplay}</Metric>
                                        </div>
                                        <FiTrendingUp size={24} color="#F59E0B" />
                                    </Flex>
                                </Card>
                            </Col>

                            <Col xs={12} sm={6} md={3}>
                                <Card decoration="top" decorationColor="violet">
                                    <Flex alignItems="start">
                                        <div>
                                            <Text>Overall Average</Text>
                                            <Metric>{reportData.summary.averages.overallDisplay}</Metric>
                                        </div>
                                        <FiAward size={24} color="#8B5CF6" />
                                    </Flex>
                                </Card>
                            </Col>
                        </Row>

                        {/* Category Leaders */}
                        <Row className="mt-4 g-3">
                            <Col xs={12} sm={6}>
                                {renderCategoryLeader(
                                    "Quality Leader",
                                    reportData.categoryLeaders?.quality,
                                    "qualityDisplay"
                                )}
                            </Col>

                            <Col xs={12} sm={6}>
                                {renderCategoryLeader(
                                    "Staff Leader",
                                    reportData.categoryLeaders?.staff,
                                    "staffDisplay"
                                )}
                            </Col>
                        </Row>

                        {/* Full Rankings Table */}
                        <Card className="mt-4">
                            <Title>Complete Rankings</Title>
                            <Text className="mb-4">All airports ranked by overall performance</Text>

                            <div style={styles.tableContainer}>
                                <table style={styles.table}>
                                    <thead>
                                        <tr style={styles.tableHeader}>
                                            <th style={styles.th}>Rank</th>
                                            <th style={styles.th}>Airport</th>
                                            <th style={styles.th}>Reviews</th>
                                            <th style={styles.th}>Quality</th>
                                            <th style={styles.th}>Staff</th>
                                            <th style={styles.th}>Overall</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {(reportData.rankings || []).length ? (
                                            reportData.rankings.map((airport) => (
                                                <tr key={airport.airport} style={styles.tableRow}>
                                                    <td style={styles.td}>
                                                        <span style={{
                                                            ...styles.rankCircle,
                                                            backgroundColor: getRankColor(airport.rank),
                                                        }}>
                                                            {airport.rank}
                                                        </span>
                                                    </td>
                                                    <td style={{ ...styles.td, fontWeight: 600 }}>
                                                        {airport.airport}
                                                    </td>
                                                    <td style={styles.td}>{airport.reviews}</td>
                                                    <td style={styles.td}>
                                                        <span style={styles.scoreCell(airport.quality)}>
                                                            {airport.qualityDisplay}
                                                        </span>
                                                    </td>
                                                    <td style={styles.td}>
                                                        <span style={styles.scoreCell(airport.staff)}>
                                                            {airport.staffDisplay}
                                                        </span>
                                                    </td>
                                                    <td style={styles.td}>
                                                        <span style={{
                                                            ...styles.scoreCell(airport.overall),
                                                            fontWeight: 700,
                                                        }}>
                                                            {airport.overallDisplay}
                                                        </span>
                                                    </td>
                                                </tr>
                                            ))
                                        ) : (
                                            <tr style={styles.tableRow}>
                                                <td colSpan={6} style={styles.emptyTableCell}>
                                                    No rankings available for the selected filters
                                                </td>
                                            </tr>
                                        )}
                                    </tbody>
                                </table>
                            </div>
                        </Card>

                        {/* Individual Reviews (only when includeReviews was requested) */}
                        {Array.isArray(reportData.reviews) && (
                            <Card className="mt-4">
                                <Title>Individual Reviews</Title>
                                <Text className="mb-4">
                                    {reportData.reviews.length} review{reportData.reviews.length === 1 ? "" : "s"} for{" "}
                                    {reportData.rankings?.[0]?.airport || selectedAirports[0]}
                                </Text>

                                <div style={styles.tableContainer}>
                                    <table style={styles.table}>
                                        <thead>
                                            <tr style={styles.tableHeader}>
                                                <th style={styles.th}>Passenger</th>
                                                <th style={styles.th}>Date</th>
                                                <th style={styles.th}>Flight</th>
                                                <th style={styles.th}>Quality</th>
                                                <th style={styles.th}>Staff</th>
                                                <th style={styles.th}>Comments</th>
                                                <th style={styles.th}>Waiting Time</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {reportData.reviews.length ? (
                                                reportData.reviews.map((rev, idx) => (
                                                    <tr key={rev.id || idx} style={styles.tableRow}>
                                                        <td style={styles.td}>{rev.name || "—"}</td>
                                                        <td style={styles.td}>{formatReviewDate(rev.date)}</td>
                                                        <td style={styles.td}>{rev.flightNumber || "—"}</td>
                                                        <td style={styles.td}>{renderStars(rev.quality)}</td>
                                                        <td style={styles.td}>{renderStars(rev.staff)}</td>
                                                        <td style={{ ...styles.td, maxWidth: 360, whiteSpace: "pre-wrap" }}>
                                                            {rev.comments || "—"}
                                                        </td>
                                                        <td style={styles.td}>{rev.waitingTime || "—"}</td>
                                                    </tr>
                                                ))
                                            ) : (
                                                <tr style={styles.tableRow}>
                                                    <td colSpan={7} style={styles.emptyTableCell}>
                                                        No individual reviews for this airport in the selected period
                                                    </td>
                                                </tr>
                                            )}
                                        </tbody>
                                    </table>
                                </div>
                            </Card>
                        )}

                        {/* Airports with No Reviews */}
                        {reportData.noReviewAirports && reportData.noReviewAirports.length > 0 && (
                            <Card className="mt-4">
                                <Title>Airports with No Reviews</Title>
                                <Text className="mb-3">These airports had no reviews during the selected period</Text>
                                <div style={styles.noReviewGrid}>
                                    {reportData.noReviewAirports.map(airport => (
                                        <Badge
                                            key={airport}
                                            bg="secondary"
                                            style={styles.noReviewBadge}
                                        >
                                            {airport}
                                        </Badge>
                                    ))}
                                </div>
                            </Card>
                        )}

                        {/* Report Footer */}
                        <div style={styles.reportFooter}>
                            <Text style={{ color: Colors.GRAY_DARK, fontSize: 12 }}>
                                Report generated on {new Date(reportData.generatedAt).toLocaleString()}
                            </Text>
                        </div>
                    </div>
                </>
            )}

            {/* Empty State */}
            {!loading && !reportData && !error && (
                <Card className="text-center py-5">
                    <FiAward size={48} color={Colors.GRAY_MEDIUM} />
                    <Title className="mt-3">No Report Generated</Title>
                    <Text>Select filters and click "Generate Report" to view performance rankings</Text>
                </Card>
            )}
        </div>
    );
};

const styles = {
    label: {
        fontSize: 13,
        fontWeight: 500,
        color: Colors.TEXT_COLOR,
        marginBottom: 4,
    },
    input: {
        borderRadius: 8,
        fontSize: 14,
    },
    generateBtn: {
        width: "100%",
        padding: "10px 20px",
        borderRadius: 8,
        backgroundColor: Colors.PRM_LIGHT_BLUE,
        border: "none",
        fontWeight: 500,
    },
    downloadBtn: {
        display: "flex",
        alignItems: "center",
        gap: 8,
        padding: "10px 20px",
        borderRadius: 8,
    },
    addAirportRow: {
        display: "flex",
        flexWrap: "wrap",
        gap: 8,
        alignItems: "center",
        marginBottom: 10,
    },
    airportCodeInput: {
        width: 92,
        textTransform: "uppercase",
        letterSpacing: 0,
    },
    addAirportBtn: {
        borderRadius: 8,
        fontSize: 13,
        fontWeight: 500,
        padding: "7px 14px",
    },
    airportCodeError: {
        color: "#dc2626",
        fontSize: 12,
        margin: 0,
    },
    airportGrid: {
        display: "flex",
        flexWrap: "wrap",
        gap: 8,
        alignItems: "center",
    },
    airportBadge: {
        padding: "8px 16px",
        fontSize: 13,
        fontWeight: 500,
        borderRadius: 6,
    },
    reportHeader: {
        background: `linear-gradient(135deg, ${Colors.PRM_LIGHT_BLUE} 0%, #1e40af 100%)`,
        padding: "24px",
        borderRadius: 12,
        marginBottom: 16,
    },
    rankBadge: (rank) => ({
        position: "absolute",
        top: 12,
        left: "50%",
        transform: "translateX(-50%)",
        fontSize: 28,
    }),
    tableContainer: {
        overflowX: "auto",
    },
    table: {
        width: "100%",
        borderCollapse: "collapse",
    },
    tableHeader: {
        backgroundColor: Colors.GRAY_LIGHT,
    },
    th: {
        padding: "12px 16px",
        textAlign: "left",
        fontSize: 13,
        fontWeight: 600,
        color: Colors.TEXT_COLOR,
        borderBottom: `2px solid ${Colors.GRAY_MEDIUM}`,
    },
    tableRow: {
        borderBottom: `1px solid ${Colors.GRAY_LIGHT}`,
    },
    td: {
        padding: "12px 16px",
        fontSize: 14,
    },
    emptyTableCell: {
        padding: "20px 16px",
        textAlign: "center",
        color: Colors.GRAY_DARK,
        fontSize: 14,
    },
    emptyMetric: {
        padding: "18px 0",
        textAlign: "center",
    },
    rankCircle: {
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        width: 28,
        height: 28,
        borderRadius: "50%",
        color: "#fff",
        fontWeight: 600,
        fontSize: 12,
    },
    scoreCell: (score) => ({
        padding: "4px 10px",
        borderRadius: 4,
        backgroundColor: score >= 4.5 ? "#d1fae5" : score >= 4.0 ? "#dcfce7" : score >= 3.5 ? "#fef9c3" : "#fee2e2",
        color: score >= 4.0 ? "#065f46" : score >= 3.5 ? "#854d0e" : "#991b1b",
        fontSize: 13,
    }),
    noReviewGrid: {
        display: "flex",
        flexWrap: "wrap",
        gap: 8,
    },
    noReviewBadge: {
        padding: "8px 16px",
        fontSize: 13,
    },
    reportFooter: {
        marginTop: 24,
        paddingTop: 16,
        borderTop: `1px solid ${Colors.GRAY_LIGHT}`,
        textAlign: "center",
    },
};

export default PerformanceReport;
